import { AppType } from "../../../constants/AppType";
import {
  DOCUMENTS_PATH,
  HANDBOOK_PATH,
  HOME_PATH,
  JOBS_PATH,
  LEAVE_PATH,
  MESSAGES_PATH,
  PROFILE_PATH,
  PWA_PATH,
  TIMESHEETS_OVERVIEW_PATH,
  WORKSCHEDULE_PATH,
} from "../../../constants/CvpPathsAndParams";
import { getAppType, isAppTypeIframe } from "../../../getAppType";
import { useLocStore } from "../../../localization";
import { LocContext } from "../../../localization/LocContext";
import { useExtraLinks } from "../../../settings/extraLinks";
import { useSettings } from "../../../settings/useSettings";
import { useLogOut } from "../../../url/logOut";
import { getRedirectProps } from "../../../url/redirect";
import { useCurrentPath } from "../../../url/useCurrentPath";
import { getLoginProps, useLoginProps } from "../../../url/useLoginProps";
import { useSearchParam } from "../../../url/useSearchParam";
import { CVPSearchParams } from "../../../url/wsQueryStringStore";
import { PathType, path } from "../../../url/WSRouter";
import { useWhoAmI } from "../../../user/useWhoAmI";
import {
  useEnableParameterOverrides,
  useShowVpnTools,
} from "../../adminSettings/AdminContext";
import { doesPathRequireLogin } from "../../Routes";
import { CustomMenuItem, isPathSelected } from "./completeMenuItem";

export interface UseNavItemsProps {
  showLinksThatRequireLogin?: boolean;
  showHome?: boolean;
  showJobs?: boolean;
}
export function useNavItems(props?: UseNavItemsProps): {
  navItems: CustomMenuItem[];
  loginOutItems: CustomMenuItem[];
} {
  const {
    showLinksThatRequireLogin = true,
    showHome = true,
    showJobs = false,
  } = props || {};

  const L = useLocStore(LocContext.GENERAL);

  const settings = useSettings();
  const currentPath = useCurrentPath();

  const { confirmedLoggedIn, confirmedNotLoggedIn } = useWhoAmI();

  const loginProps = useLoginProps();
  const extraLinks = useExtraLinks();
  const logOut = useLogOut();
  const showVpnTools = useShowVpnTools();
  const enableParameterOverrides = useEnableParameterOverrides();

  const cvppwa = !!useSearchParam(CVPSearchParams.cvppwa);

  function _getRedirectProps(path: string) {
    path = path.replace(/^\//, "");
    const requiresLogin = doesPathRequireLogin(path);

    if (requiresLogin && isAppTypeIframe() && settings.iframe_loginOldPortal) {
      return loginProps.redirectProps;
    }

    if (requiresLogin && confirmedNotLoggedIn) {
      return getLoginProps(currentPath, settings, {
        cvpalru: path,
        enableParameterOverrides,
        showVpnTools,
        cvppwa,
      }).redirectProps;
    }

    return getRedirectProps(path, { mergeQs: {} });
  }

  const navItems: CustomMenuItem[] = [];

  if (showHome) {
    navItems.push({
      ..._getRedirectProps(HOME_PATH),
      label: L.id("7cfdece91dd3").loc("Hjem"),
      icon: "pi pi-fw pi-home",

      isSelected: () => isPathSelected([path("/"), path("frontpage")]),
    });
  }

  if (
    settings.pwa_enabled &&
    getAppType() === AppType.cvWebtempNo &&
    showLinksThatRequireLogin
  ) {
    navItems.push({
      ..._getRedirectProps(PWA_PATH),
      label: L.id("7a821bbfcf26").loc("App og push-varsler"),
      icon: "pi pi-fw pi-mobile",
      isSelected: () => isPathSelected(path("pwa", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (showJobs) {
    navItems.push({
      ..._getRedirectProps(JOBS_PATH),
      label: L.id("9218a26bd617").loc("Ledige stillinger"),
      icon: "pi pi-fw pi-briefcase",
      isSelected: () => isPathSelected(path("jobs", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(PROFILE_PATH),
      label: L.id("fc33916374e4").loc("Profil"),
      icon: "pi pi-fw pi-user-edit",
      isSelected: () => isPathSelected(path("profile", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (settings.timesheets_enabled && showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(TIMESHEETS_OVERVIEW_PATH),
      label: L.id("fd1993c883d4").loc("Timeliste"),
      icon: "pi pi-fw pi-clock",

      isSelected: () =>
        isPathSelected([
          path("timesheets", PathType.EXACT_OR_SUBPATH),
          path("timesheet", PathType.EXACT_OR_SUBPATH),
        ]),
    });
  }

  if (settings.messages_enabled && showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(MESSAGES_PATH),
      label: L.id("c32e03eb24f1").loc("Postkasse"),
      icon: "pi pi-fw pi-envelope",

      isSelected: () => isPathSelected(path("messages", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(DOCUMENTS_PATH),
      label: L.id("28f2b77e6e7a").loc("Dokumenter"),
      icon: "pi pi-fw pi-file",

      isSelected: () =>
        isPathSelected(path("profile/documents", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (settings.workschedule_enabled && showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(WORKSCHEDULE_PATH),
      label: L.id("9e5f41db3b47").loc("Arbeidsoversikt"),
      icon: "pi pi-fw pi-calendar",
      isSelected: () => isPathSelected(path("workschedule", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (settings.handbook_enabled && showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(HANDBOOK_PATH),
      label: L.id("d2f6a94b7e13").loc("Personalhåndbok"),
      icon: "pi pi-fw pi-book",
      isSelected: () => isPathSelected(path("handbook", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (settings.leave_enabled && showLinksThatRequireLogin) {
    navItems.push({
      ..._getRedirectProps(LEAVE_PATH),
      label: L.id("8a1f4c2b9e7d").loc("Ferie og fravær"),
      icon: "pi pi-fw pi-calendar-times",
      isSelected: () => isPathSelected(path("leave", PathType.EXACT_OR_SUBPATH)),
    });
  }

  navItems.push(
    ...extraLinks.map((link) => {
      return {
        isSelected: () => false,
        target: "_blank",
        href: link.url,
        label: link.text,
        icon: "pi pi-fw pi-external-link",
      } as CustomMenuItem;
    })
  );

  const loginOutItems: CustomMenuItem[] = [];

  if (confirmedNotLoggedIn) {
    loginOutItems.push({
      ..._getRedirectProps(PROFILE_PATH),
      label: L.id("0fb4e5d60bd0").loc("Logg inn/Registrer CV"),
      icon: "pi pi-fw pi-sign-in",
      isSelected: () => isPathSelected(path("profile", PathType.EXACT_OR_SUBPATH)),
    });
  }

  if (confirmedLoggedIn) {
    loginOutItems.push({
      label: L.id("9bfb3315c7f8").loc("Logg ut"),
      icon: "pi pi-fw pi-sign-out",
      className: "log-out",
      onClick: logOut,
    });
  }

  return { navItems, loginOutItems };
}
